/**
 * Saying one word and getting it scored — E4's production practice.
 *
 * The clinic's discrimination drill trains perception: hear two, pick which. E4 puts
 * production practice after it, "with per-attempt scoring", and that is the half that can
 * actually clear a pronunciation mistake: `recordPhonemeOutcomes` on the server closes one
 * when the sound comes out right in a scored utterance, and nothing but a spoken attempt
 * satisfies that rule.
 *
 * The flow is the onboarding first-utterance flow with a different target sentence —
 * record, upload to R2 through a presigned URL so audio never routes through the Worker
 * body, then score against the word the learner was asked to say. Scoring against anything
 * else would mark a correct utterance wrong.
 */

import { clientUlid, type VibeLangClient } from '@vibelang/api-client';
import { Recorder, type RecordingResult, uploadContentType } from './audio.js';

/** What one attempt at a word came to. */
export interface WordAttempt {
  readonly status: 'scored' | 'withheld' | 'failed';
  /** The sentence-level score, or null when none was given. */
  readonly score: number | null;
  /** What to tell the learner. Null when the score speaks for itself. */
  readonly message: string | null;
}

/**
 * Below this there is not enough speech to judge, so it is never sent.
 *
 * The same threshold the first-utterance flow uses, and withheld locally for the same
 * reason: it is both honest and free. A scorer asked to judge silence either guesses or
 * declines, and paying a vendor to decline is the worst of the three outcomes.
 */
const MINIMUM_SPEECH_RATIO = 0.15;

export async function practiseWord(
  api: VibeLangClient,
  target: { courseId: string; word: string },
  recorder: Recorder = new Recorder(),
): Promise<WordAttempt> {
  let result: RecordingResult;
  try {
    await recorder.open();
    recorder.start();
    result = await recorder.stop();
  } catch {
    // A refused microphone is not a failed attempt at the sound, and must not be
    // reported as one.
    return {
      status: 'failed',
      score: null,
      message: "We couldn't use your microphone for that one.",
    };
  }

  if (result.signal.speechRatio < MINIMUM_SPEECH_RATIO) {
    return {
      status: 'withheld',
      score: null,
      message: "We didn't catch enough to score that — try once more.",
    };
  }

  try {
    const contentType = uploadContentType(result.mimeType);
    const upload = await api.call('createAudioUpload', {
      body: {
        sessionId: null,
        contentType,
        bytes: result.blob.size,
        durationMs: result.durationMs,
      },
    });
    await fetch(upload.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': contentType },
      body: result.blob,
    });

    const feedback = await api.call('scoreSpeech', {
      body: {
        // The shared generator, not a UUID with its hyphens filed off: these ids are
        // ULIDs, the server sorts and deduplicates on them, and a client-generated one is
        // what makes a retried write idempotent.
        id: clientUlid(),
        courseId: target.courseId,
        audioKey: upload.audioKey,
        // The word the learner was asked to say, and the one the clinic is about.
        expectedText: target.word,
        durationMs: result.durationMs,
        // Measured on-device so the server can decline rather than guess at a bad one.
        signal: result.signal,
        wantPhonemeDetail: true,
      },
    });

    // `withheld` is a first-class outcome in this product, not an error: the clinic says
    // so rather than showing a number nobody stands behind.
    if (feedback.status !== 'scored') {
      return {
        status: 'withheld',
        score: null,
        message: feedback.message ?? "We didn't score that one.",
      };
    }
    return { status: 'scored', score: feedback.overall ?? null, message: null };
  } catch {
    return {
      status: 'failed',
      score: null,
      message: "We couldn't score that just now.",
    };
  }
}
